import { restClient } from '@/Core'

import { ACTIVITY_REST_URL, OWNER_FIELDS } from '../Contracts/Activity.types'

import type {
    ActivityCollectionResponse,
    ActivityOwnerKind,
    ActivityRecord,
} from '../Contracts/Activity.types'

export interface LoadOwnerActivitiesParams {
    owner: ActivityOwnerKind
    ownerUuid: string
    /** Код статусу або `undefined` — «Всі» */
    status?: string | undefined
    page: number
    pageSize: number
}

/** Умова префільтра власника (`SPEC-011` §8.6, В5): `client.uuid` / `carrier.uuid`. */
export const ownerCondition = (owner: ActivityOwnerKind, uuid: string) => ({
    field: `${OWNER_FIELDS[owner].owner}.uuid`,
    op: '=',
    value: uuid,
})

/**
 * Справи одного власника для вкладки «Справи» його картки — сторінкою
 * спільної колекції `GET /services/activities` із префільтром власника.
 * Порядок і правило рядка — бекові (`updatedAt DESC`, відповідальний ∨
 * автор ∨ адміністратор, `SPEC-011` §10), права рядка — `_permissions`.
 *
 * `forceRefresh`: ядровий клієнт дедуплікує GET на 30 с, і щойно створена чи
 * завершена справа без нього в списку не з'явилася б.
 */
export const LoadOwnerActivitiesTask = async ({
    owner,
    ownerUuid,
    status,
    page,
    pageSize,
}: LoadOwnerActivitiesParams): Promise<{ items: ActivityRecord[]; total: number }> => {
    const conditions: unknown[] = [ownerCondition(owner, ownerUuid)]

    if (status) conditions.push({ field: 'status.code', op: '=', value: status })

    const raw = await restClient.get(
        `${ACTIVITY_REST_URL}?page=${page}&limit=${pageSize}&permissions=true&filters=${encodeURIComponent(
            JSON.stringify({ and: conditions })
        )}`,
        undefined,
        { forceRefresh: true }
    )
    const data = raw as unknown as ActivityCollectionResponse | ActivityRecord[] | null
    const list = Array.isArray(data) ? data : (data?.items ?? data?.['hydra:member'] ?? [])
    const items = Array.isArray(list) ? list.filter((item) => typeof item?.uuid === 'string') : []
    const total = !Array.isArray(data) && typeof data?.meta?.total === 'number' ? data.meta.total : items.length

    return { items, total }
}
